import { LIBRARY_TYPES, CONTENT_TYPES } from '@go-modules/components/library/library-collections-viewer/library-collection-viewer.constants';
import { DESTINATION_MODES } from './collection-destination.constants';
import { UserService, userServiceToken } from 'go-modules/models/user/user.service';
import { FULLSTORY_EVENTS } from 'go-modules/services/fullstory/fullstory.events';
import { FullstoryService, fullstoryToken } from 'go-modules/services/fullstory/fullstory.service';
import { Component, DestroyRef, ElementRef, Inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { LibraryService, LibraryCollectionFolder } from '@go-modules/services/library/library.service';
import { MatDialog } from '@angular/material/dialog';
import { GO_MODAL_DATA } from '@go-modules/services/go-modal/go-modal.tokens';
import { GoDialogRef } from '@go-modules/services/go-dialog-ref/go-dialog-ref';
import { MessageDialogComponent } from '../../dialogs/message-dialog/message-dialog.component';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { BehaviorSubject } from 'rxjs';
import { GoModalService } from '@go-modules/services/go-modal/go-modal.service';
import { ManageLibraryCollectionFolderComponent } from '../manage-library-collection-folder/manage-library-collection-folder.component';
import { ConfirmDialogComponent, ConfirmDialogData } from '@go-modules/components/dialogs/confirm-dialog/confirm-dialog.component';
import { NgxActivityService } from '@go-modules/services/activity/activity.service';
import type {
	LibraryCollection,
	LibraryCollectionItem
} from '@go-modules/interfaces/library/library-collection';
import { UniqueIdGeneratorUtil } from '@go-modules/utilities/unique-id-generator/unique-id-generator.util';

export interface CollectionDestinationData {
	collections: LibraryCollection[];
	selectedCollection: LibraryCollection;
	contentType: string;
	mode: DESTINATION_MODES;
	defaultFolderId?: number | null;
	allowSkip?: boolean;
	name?: string | null;
	showNameField?: boolean;
	item?: LibraryCollectionItem;
	submitText?: string;
}

export interface PseudoFolderNone {
	id: number;
	name: string;
}

export type FolderOption = LibraryCollectionFolder | PseudoFolderNone;

@Component({
	selector: 'ngx-collection-destination',
	templateUrl: './collection-destination.component.html',
	styleUrls: ['./collection-destination.component.scss'],
	standalone: false
})
export class CollectionDestinationComponent implements OnInit {
	public dialogTitleId = `collection-destination-dialog-title-${UniqueIdGeneratorUtil.getId()}`;
	public collections: LibraryCollection[] = [];
	public folders: LibraryCollectionFolder[] = [];
	public pendingDefaultFolder: LibraryCollectionFolder = null;
	public form = new UntypedFormGroup({
		name: new UntypedFormControl('', []),
		selectedCollection: new UntypedFormControl(null, [Validators.required]),
		selectedFolder: new UntypedFormControl(null, [])
	});
	public foldersLoading$: BehaviorSubject<boolean> = new BehaviorSubject(true);
	public hasFolderAdded = false;

	constructor (
		public dialogRef: GoDialogRef,
		private activityService: NgxActivityService,
		private libraryService: LibraryService,
		private translate: TranslateService,
		private dialog: MatDialog,
		private modal: GoModalService,
		private elementRef: ElementRef,
		private destroyRef: DestroyRef,
		@Inject(GO_MODAL_DATA) public data: CollectionDestinationData,
		@Inject(userServiceToken) private userService: UserService,
		@Inject(fullstoryToken) private fullstoryService: FullstoryService
	) {}

	public ngOnInit () {
		if (this.data.selectedCollection) {
			this.form.controls.selectedCollection.setValue(this.data.selectedCollection);
		}
		if (this.data.name) {
			this.form.controls.name.setValue(this.data.name);
		}
		if (this.data.showNameField) {
			this.form.controls.name.addValidators(Validators.required);
		}
		this.collections = this.data.collections.filter((collection) => {
			const removeTypes = [LIBRARY_TYPES.ARCHIVE, LIBRARY_TYPES.LEGACY, LIBRARY_TYPES.DIRECT_SHARE];
			const permission = collection.library_collection_permissions.find((itemPermission) => {
				return itemPermission.user_id === this.userService.currentUser.user_id;
			});
			if (!this.userService.currentUser.is_root_content) {
				removeTypes.push(LIBRARY_TYPES.MANAGED);
			}
			return !removeTypes.includes(collection.type) && permission?.is_hidden === false;
		}).sort((a, b) => {
			return a.name.localeCompare(b.name);
		});

		if (this.data.selectedCollection == null ||
			this.data.selectedCollection.type === LIBRARY_TYPES.ARCHIVE ||
			(this.data.selectedCollection.type === LIBRARY_TYPES.MANAGED &&
				!this.userService.currentUser.is_root_content) ||
			this.data.selectedCollection.type === LIBRARY_TYPES.LEGACY ||
			this.data.selectedCollection.type === LIBRARY_TYPES.DIRECT_SHARE ||
			!this.collections.some((collection) => collection.id === this.data.selectedCollection.id)
		) {
			this.form.controls.selectedCollection.setValue(this.collections.find((collection) => {
				return collection.type === LIBRARY_TYPES.MINE;
			}) ?? this.collections[0]);
		} else {
			this.form.controls.selectedCollection.setValue(this.data.selectedCollection);
		}

		this.loadFolders();
	}

	public loadFolders () {
		if (this.form.value.selectedCollection == null) {
			this.folders = [];
			this.foldersLoading$.next(false);
			return;
		}

		this.foldersLoading$.next(true);
		this.form.controls.selectedFolder.setValue(null);
		this.libraryService.getCollectionFolders(this.form.value.selectedCollection.id)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((folders) => {
				this.folders = folders.sort((a, b) => {
					return a.name.localeCompare(b.name);
				});
				this.foldersLoading$.next(false);

				if (this.data.defaultFolderId) {
					const selectedFolder = this.folders.find((folder) => {
						return folder.id === this.data.defaultFolderId;
					});
					if (selectedFolder) {
						this.pendingDefaultFolder = selectedFolder;
					}
				}
			});
	}

	public onAvailableFoldersChange (availableFolders: LibraryCollectionFolder[]) {
		if (this.pendingDefaultFolder) {
			const isAvailable = availableFolders.some((f) => f.id === this.pendingDefaultFolder.id);
			this.form.controls.selectedFolder.setValue(isAvailable ? this.pendingDefaultFolder : null);
			this.pendingDefaultFolder = null;
		}
	}

	public openAddFolderModal () {
		this.fullstoryService.createEvent(FULLSTORY_EVENTS.NEW_LIBRARY_CREATE_FOLDER_CLICK, {});

		if (this.folders.length >= 100) {
			return MessageDialogComponent.open(this.dialog, {
				data: {
					title: this.translate.instant('library-collections_max-folders-title'),
					message: this.translate.instant('library-collections_max-folders-message')
				}
			});
		}

		return this.modal.open(ManageLibraryCollectionFolderComponent, false, {
			data: {
				createMode: true,
				existingFolders: this.folders,
				collections: this.collections,
				selectedCollection: this.form.value.selectedCollection,
				folder: this.form.value.selectedFolder
			}
		}).afterClosed()
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((res) => {
				const toggleButton = this.elementRef.nativeElement.querySelector('.add-btn');
				toggleButton.focus();
				if (res.dismissed) {
					return;
				}
				if (res.request_failed) {
					return this.dialog.open(MessageDialogComponent, {
						data: {
							title: this.translate.instant('library-collections_add-folder-fail-title'),
							message: this.translate.instant('library-collections_add-folder-fail-message')
						}
					});
				}
				const newFolder = res;
				this.fullstoryService.createEvent(FULLSTORY_EVENTS.NEW_LIBRARY_FOLDER_CREATED, {});
				this.hasFolderAdded = true;

				if (this.form.value.selectedCollection.id === newFolder.library_collection_id) {
					this.folders = [...this.folders, newFolder].sort((a, b) => {
						return a.name.localeCompare(b.name);
					});

					// Store as pending for when tree component emits available folders
					this.pendingDefaultFolder = newFolder;
				} else {
					const selectedCollection = this.collections.find((collection) => {
						return collection.id === newFolder.library_collection_id;
					});
					this.form.controls.selectedCollection.setValue(selectedCollection);
					this.data.defaultFolderId = newFolder.id;
					this.loadFolders();
				}
			});
	}

	public dismiss () {
		if (this.hasFolderAdded) {
			this.dialogRef.close({
				dismissed: true,
				hasFolderAdded: this.hasFolderAdded,
				collection: this.form.value.selectedCollection
			});
		} else {
			this.dialogRef.close({dismissed: true});
		}
	}

	public close () {
		if (this.data.contentType === CONTENT_TYPES.ACTIVITIES && this.data.showNameField) {
			if (this.form.value.name && this.form.value.name !== '') {
				this.save(/*additionalData*/ {name: this.form.value.name});
			}
		} else if (this.data.mode === DESTINATION_MODES.MOVE &&
			this.data.contentType === CONTENT_TYPES.ACTIVITIES &&
			this.data.item.parent_child_sync &&
			this.form.value.selectedCollection.type !== LIBRARY_TYPES.ORG) {
			this.activityService.getChildrenCount(this.data.item.item_id)
				.pipe(takeUntilDestroyed(this.destroyRef))
				.subscribe({
					next: (result) => {
						if (result.activities > 0) {
							this.launchMoveWarningModal(result.activities);
						} else {
							this.save();
						}
					},
					error: () => {
						this.launchMoveWarningModal('');
					}
				});
		} else {
			this.save();
		}
	}

	public skipCollection () {
		this.dialogRef.close({
			autoSelect: true,
			skipCollection: true
		});
	}

	private save (additionalData = {}) {
		this.dialogRef.close({
			collection: this.form.value.selectedCollection,
			folder: this.form.value.selectedFolder,
			autoSelect: this.data.allowSkip ? true : false,
			hasFolderAdded: this.hasFolderAdded,
			...additionalData
		});
	}

	private launchMoveWarningModal (copies: string|number) {
		this.dialog.open(
			ConfirmDialogComponent, {
				data: {
					title: this.translate.instant('library-collections_item_move_master-activity-title'),
					message: this.translate.instant('library-collections_item_move_master-activity-warning', {copies}),
					confirmText: this.translate.instant('collection-destination_title-activity-move'),
					cancelText: this.translate.instant('common_cancel')
				} as ConfirmDialogData
			})
			.afterClosed()
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((confirm) => {
				if (confirm) {
					this.save();
				}
			});
	}
}
